import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { AuthContextType, LoginCredentials, RegisterCredentials, User } from '../types/auth';
import { authService } from '../services/auth.service';
import { MobileLoadingIndicator } from '../components/ui';
import { isTokenExpired, getTokenExpirationTime } from '../utils/tokenUtils';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => authService.getUser());
  const [isLoading, setIsLoading] = useState(true);
  const tokenExpirationTimerRef = useRef<number | null>(null);

  // Set up token expiration timer
  const setupTokenExpirationTimer = () => {
    // Clear any existing timer
    if (tokenExpirationTimerRef.current) {
      window.clearTimeout(tokenExpirationTimerRef.current);
      tokenExpirationTimerRef.current = null;
    }

    const token = authService.getToken();
    if (!token) return;

    // Check if token is already expired
    if (isTokenExpired(token)) {
      console.log('Token is already expired');
      authService.logout();
      setUser(null);
      window.location.href = '/login?session_expired=true';
      return;
    }

    // Get token expiration time
    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) return;

    // Calculate time until expiration (subtract 10 seconds to ensure we logout before actual expiration)
    const timeUntilExpiration = expirationTime - Date.now() - 10000;
    
    if (timeUntilExpiration <= 0) {
      // Token is about to expire or already expired
      authService.logout();
      setUser(null);
      window.location.href = '/login?session_expired=true';
      return;
    }

    console.log(`Setting token expiration timer for ${timeUntilExpiration / 1000} seconds`);
    
    // Set timer to logout when token expires
    tokenExpirationTimerRef.current = window.setTimeout(() => {
      console.log('Token expired, logging out');
      authService.logout();
      setUser(null);
      window.location.href = '/login?session_expired=true';
    }, timeUntilExpiration);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have a stored token
        const token = authService.getToken();
        if (token) {
          // Check if token is expired before making API call
          if (isTokenExpired(token)) {
            console.log('Token is expired, redirecting to login');
            authService.logout();
            setUser(null);
            window.location.href = '/login?session_expired=true';
            return;
          }

          // Validate the token and get user data
          const userData = await authService.validateToken();
          setUser(userData);
          
          // Set up token expiration timer
          setupTokenExpirationTimer();
        }
      } catch (error: any) {
        console.error('Auth initialization failed:', error);
        // Clear invalid auth state
        authService.logout();
        setUser(null);

        // If the error is due to an expired token, redirect to login with session expired flag
        if (error.response?.status === 401) {
          window.location.href = '/login?session_expired=true';
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Cleanup function to clear the timer
    return () => {
      if (tokenExpirationTimerRef.current) {
        window.clearTimeout(tokenExpirationTimerRef.current);
      }
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const { user } = await authService.login(credentials);
      setUser(user);
      // Set up token expiration timer after successful login
      setupTokenExpirationTimer();
    } catch (error: any) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials, rolePath?: string) => {
    try {
      const { user } = await authService.register(credentials, rolePath);
      setUser(user);
      // Set up token expiration timer after successful registration
      setupTokenExpirationTimer();
    } catch (error: any) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear token expiration timer
    if (tokenExpirationTimerRef.current) {
      window.clearTimeout(tokenExpirationTimerRef.current);
      tokenExpirationTimerRef.current = null;
    }
    
    authService.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
  };

  if (isLoading) {
    return <MobileLoadingIndicator />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};